import L from "leaflet";

export const STAGE_COLORS = {
  booked: "#3b82f6",
  transit: "#f59e0b",
  delivered: "#10b981",
  won: "#64748b",
};

export function arcLatLngs(o, d, n = 56, curve = 0.18) {
  const lat1 = o.lat, lng1 = o.lng, lat2 = d.lat, lng2 = d.lng;
  const dLat = lat2 - lat1, dLng = lng2 - lng1;
  const dist = Math.hypot(dLat, dLng) || 1;
  const perpLat = -dLng / dist, perpLng = dLat / dist;
  const off = dist * curve;
  const cLat = (lat1 + lat2) / 2 + perpLat * off;
  const cLng = (lng1 + lng2) / 2 + perpLng * off;
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n, mt = 1 - t;
    pts.push(L.latLng(
      mt * mt * lat1 + 2 * mt * t * cLat + t * t * lat2,
      mt * mt * lng1 + 2 * mt * t * cLng + t * t * lng2,
    ));
  }
  return pts;
}

export function pointAlong(pts, t) {
  const i = Math.min(pts.length - 2, Math.max(0, Math.floor(t * (pts.length - 1))));
  const f = t * (pts.length - 1) - i;
  return {
    x: pts[i].x + (pts[i + 1].x - pts[i].x) * f,
    y: pts[i].y + (pts[i + 1].y - pts[i].y) * f,
  };
}

// 60fps canvas overlay: glowing status-colored arcs, flowing comet pulses, origin radar rings.
export class FlowLayer {
  constructor(map, routes) {
    this.map = map;
    this.canvas = document.createElement("canvas");
    Object.assign(this.canvas.style, {
      position: "absolute", top: "0", left: "0", pointerEvents: "none", zIndex: 450,
    });
    map.getContainer().appendChild(this.canvas);
    this.setRoutes(routes);
    this._onResize = () => this._size();
    map.on("resize", this._onResize);
    this._size();
    this._frame = this._frame.bind(this);
    this._raf = requestAnimationFrame(this._frame);
  }

  setRoutes(routes) {
    this.items = (routes || []).map((rt, i) => ({
      rt,
      pts: arcLatLngs(rt.origin, rt.destination),
      phase: (i * 0.37) % 1,
    }));
  }

  _size() {
    const sz = this.map.getSize();
    this.dpr = window.devicePixelRatio || 1;
    this.canvas.width = sz.x * this.dpr;
    this.canvas.height = sz.y * this.dpr;
    this.canvas.style.width = `${sz.x}px`;
    this.canvas.style.height = `${sz.y}px`;
  }

  _frame(now) {
    const map = this.map;
    if (!map || !this.canvas.isConnected) return;
    const sz = map.getSize();
    if (this.canvas.width !== Math.round(sz.x * (window.devicePixelRatio || 1))) this._size();
    const ctx = this.canvas.getContext("2d");
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, sz.x, sz.y);
    const t = now / 1000;

    for (const item of this.items) {
      const proj = item.pts.map((ll) => map.latLngToContainerPoint(ll));
      const onScreen = proj.some((p) => p.x > -80 && p.x < sz.x + 80 && p.y > -80 && p.y < sz.y + 80);
      if (!onScreen) continue;
      const stage = item.rt.stage || "won";
      const color = STAGE_COLORS[stage] || STAGE_COLORS.won;

      ctx.beginPath();
      proj.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.strokeStyle = stage === "delivered" ? `${color}88` : `${color}50`;
      ctx.lineWidth = stage === "transit" ? 2 : 1.5;
      ctx.shadowColor = color;
      ctx.shadowBlur = stage === "transit" ? 9 : 5;
      ctx.stroke();
      ctx.shadowBlur = 0;

      if (stage !== "delivered") {
        const speed = stage === "transit" ? 0.13 : 0.06;
        const nPulse = stage === "transit" ? 2 : 1;
        for (let k = 0; k < nPulse; k++) {
          const prog = (t * speed + item.phase + k / nPulse) % 1;
          const { x, y } = pointAlong(proj, prog);
          for (let s = 1; s <= 5; s++) {
            const bp = pointAlong(proj, Math.max(0, prog - s * 0.012));
            ctx.beginPath();
            ctx.arc(bp.x, bp.y, 2.4 - s * 0.35, 0, Math.PI * 2);
            ctx.fillStyle = `${color}${Math.round((1 - s / 6) * 160).toString(16).padStart(2, "0")}`;
            ctx.fill();
          }
          const g = ctx.createRadialGradient(x, y, 0, x, y, 7);
          g.addColorStop(0, "#ffffff");
          g.addColorStop(0.3, color);
          g.addColorStop(1, `${color}00`);
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(x, y, 7, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      const o = proj[0];
      const ringT = (t * 0.75 + item.phase) % 1;
      ctx.beginPath();
      ctx.arc(o.x, o.y, 4 + ringT * 15, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(16,185,129,${(1 - ringT) * 0.55})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    this._raf = requestAnimationFrame(this._frame);
  }

  destroy() {
    cancelAnimationFrame(this._raf);
    this.map.off("resize", this._onResize);
    this.canvas.remove();
  }
}
